/**
 * FERP Data Source Browser
 *
 * Unified ingestion interface with 3 data source modes:
 *   1. Upload — drag-drop local files (reuses ChaosDocumentUploader)
 *   2. URL Fetch — enter a URL, server-side fetch + quarantine scan
 *   3. Adapter Sources — browse adapter web_sources, one-click fetch
 *
 * Plus:
 *   - LLM provider selection (AWS Bedrock / Ollama local)
 *   - Quarantine scan status display (4-stage security pipeline)
 *   - Adapter context picker (enriches ingestion with field mappings)
 *
 * ARCHITECTURE:
 * - Mantine v8.x (NOT MUI)
 * - Dumb Frontend (DRR-055): Layout driven by adapter configuration
 * - Repository pattern: All data via GraphQL / hooks
 * - Backend ID generation only
 * - Multi-tenant isolation via useCurrentTenant()
 * - Audit trail via useAuditLogger()
 *
 * DRR-072: Feeds into chaos ingestion pipeline phases 0-8
 */

import React, { useEffect } from 'react';
import {
  Tabs,
  Stack,
  Group,
  TextInput,
  Select,
  Button,
  Text,
  Title,
  Card,
  Badge,
  Alert,
  Table,
  ActionIcon,
  Tooltip,
  Divider,
  ThemeIcon,
} from '@mantine/core';
import {
  IconUpload,
  IconWorldWww,
  IconDatabase,
  IconBrain,
  IconShieldOff,
  IconAlertTriangle,
  IconCheck,
  IconX,
  IconDownload,
  IconServer,
  IconCloud,
} from '@tabler/icons-react';
import { ChaosDocumentUploader } from '../chaos/ChaosDocumentUploader';
import {
  useDataSourceBrowser,
  SOURCE_TYPES,
  type WebSource,
  type QuarantineScanStage,
} from '../hooks/useDataSourceBrowser';

// ============================================================
// Sub-Components
// ============================================================

/** Quarantine scan result display — shows 4-stage pipeline results */
function QuarantineStatusCard({ stages, overallStatus }: {
  stages: QuarantineScanStage[];
  overallStatus: string;
}) {
  const statusColor = {
    pass: 'green',
    warn: 'yellow',
    block: 'red',
    skipped: 'gray',
  }[overallStatus] || 'gray';

  const statusIcon = {
    pass: <IconCheck size={14} />,
    warn: <IconAlertTriangle size={14} />,
    block: <IconX size={14} />,
    skipped: <IconShieldOff size={14} />,
  }[overallStatus] || <IconShieldOff size={14} />;

  return (
    <Card withBorder padding="sm" mt="sm">
      <Group gap="xs" mb="xs">
        <ThemeIcon size="sm" color={statusColor} variant="light">
          {statusIcon}
        </ThemeIcon>
        <Text size="sm" fw={600}>Quarantine Scan</Text>
        <Badge size="xs" color={statusColor}>{overallStatus.toUpperCase()}</Badge>
      </Group>

      <Stack gap={4}>
        {stages.map((stage) => {
          const color = { pass: 'green', warn: 'yellow', block: 'red', skipped: 'gray' }[stage.status] || 'gray';
          const icon = { pass: '✓', warn: '⚠', block: '✗', skipped: '–' }[stage.status] || '?';
          return (
            <Group key={stage.stage} gap="xs">
              <Text size="xs" c={color} fw={600} style={{ width: 14, textAlign: 'center' }}>
                {icon}
              </Text>
              <Text size="xs" c="dimmed" style={{ width: 140 }}>
                {stage.stage.replace(/_/g, ' ')}
              </Text>
              <Text size="xs">{stage.details}</Text>
            </Group>
          );
        })}
      </Stack>
    </Card>
  );
}

/** LLM provider selector panel */
function LlmProviderSelector({
  llmProviders,
  selectedProvider,
  selectedModel,
  ollamaEndpoint,
  ollamaHealthy,
  currentModels,
  onProviderChange,
  onModelChange,
  onEndpointChange,
}: {
  llmProviders: ReturnType<typeof useDataSourceBrowser>['llmProviders'];
  selectedProvider: string;
  selectedModel: string;
  ollamaEndpoint: string;
  ollamaHealthy: boolean | null;
  currentModels: ReturnType<typeof useDataSourceBrowser>['currentModels'];
  onProviderChange: (value: string) => void;
  onModelChange: (value: string) => void;
  onEndpointChange: (value: string) => void;
}) {
  const providerOptions = llmProviders.map(p => ({
    value: p.provider_id,
    label: `${p.name}${p.pii_scrubbing ? ' (PII scrubbing)' : ''}`,
  }));

  const modelOptions = currentModels.map(m => ({
    value: m.model_id,
    label: `${m.name}${m.default ? ' (default)' : ''}`,
  }));

  return (
    <Card withBorder padding="sm">
      <Group gap="xs" mb="xs">
        <IconBrain size={16} />
        <Text size="sm" fw={600}>LLM Provider</Text>
      </Group>

      <Group grow>
        <Select
          size="sm"
          data={providerOptions}
          value={selectedProvider}
          onChange={(val) => val && onProviderChange(val)}
          leftSection={selectedProvider === 'ollama' ? <IconServer size={14} /> : <IconCloud size={14} />}
        />
        <Select
          size="sm"
          data={modelOptions}
          value={selectedModel}
          onChange={(val) => val && onModelChange(val)}
        />
      </Group>

      {selectedProvider === 'ollama' && (
        <Group mt="xs" gap="xs">
          <TextInput
            size="sm"
            style={{ flex: 1 }}
            placeholder="http://localhost:11434"
            value={ollamaEndpoint}
            onChange={(e) => onEndpointChange(e.currentTarget.value)}
          />
          {ollamaHealthy === true && (
            <Badge size="sm" color="green" leftSection={<IconCheck size={10} />}>
              Connected
            </Badge>
          )}
          {ollamaHealthy === false && (
            <Badge size="sm" color="red" leftSection={<IconX size={10} />}>
              Unreachable
            </Badge>
          )}
          {ollamaHealthy === null && (
            <Badge size="sm" color="gray">Checking...</Badge>
          )}
        </Group>
      )}

      {selectedProvider === 'bedrock' && (
        <Group mt="xs" gap="xs">
          <Badge size="xs" color="blue" variant="light">DRR-064 PII Scrubbing</Badge>
          <Badge size="xs" color="green" variant="light">AWS us-east-1</Badge>
        </Group>
      )}
    </Card>
  );
}

// ============================================================
// Main Component
// ============================================================

export interface DataSourceBrowserProps {
  /** Called when ingestion completes (any mode) */
  onIngestionComplete?: (result: { workflowId: string; state: string }) => void;
  /** Compact mode for embedding in wizards */
  compact?: boolean;
}

export function DataSourceBrowser({
  onIngestionComplete,
  compact = false,
}: DataSourceBrowserProps) {
  const browser = useDataSourceBrowser();

  // Load adapters on mount
  useEffect(() => {
    browser.loadAdapters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Adapter options for the Select dropdown
  const adapterOptions = browser.adapters.map(a => ({
    value: a.adapter_id,
    label: `${a.adapter_id} (${a.industry}/${a.jurisdiction}/${a.setting})`,
  }));

  // Source type options for the Select dropdown
  const sourceTypeOptions = SOURCE_TYPES.map(st => ({
    value: st.value,
    label: `${st.label} (${st.confidence})`,
  }));

  return (
    <Stack gap="md">
      {!compact && (
        <Title order={3}>Data Source Browser</Title>
      )}

      {/* LLM Provider Selection */}
      <LlmProviderSelector
        llmProviders={browser.llmProviders}
        selectedProvider={browser.selectedProvider}
        selectedModel={browser.selectedModel}
        ollamaEndpoint={browser.ollamaEndpoint}
        ollamaHealthy={browser.ollamaHealthy}
        currentModels={browser.currentModels}
        onProviderChange={browser.setSelectedProvider}
        onModelChange={browser.setSelectedModel}
        onEndpointChange={browser.setOllamaEndpoint}
      />

      {/* Three-Tab Ingestion Interface */}
      <Tabs defaultValue="upload">
        <Tabs.List>
          <Tabs.Tab value="upload" leftSection={<IconUpload size={14} />}>
            Upload
          </Tabs.Tab>
          <Tabs.Tab value="url" leftSection={<IconWorldWww size={14} />}>
            URL Fetch
          </Tabs.Tab>
          <Tabs.Tab value="adapter" leftSection={<IconDatabase size={14} />}>
            Adapter Sources
          </Tabs.Tab>
        </Tabs.List>

        {/* ─── Upload Tab ─────────────────────────────────── */}
        <Tabs.Panel value="upload" pt="md">
          <ChaosDocumentUploader
            compact={compact}
            onUploadComplete={(results) => {
              if (onIngestionComplete && results.length > 0) {
                onIngestionComplete({
                  workflowId: results[0]?.document?.documentId || '',
                  state: 'UPLOADED',
                });
              }
            }}
          />
        </Tabs.Panel>

        {/* ─── URL Fetch Tab ──────────────────────────────── */}
        <Tabs.Panel value="url" pt="md">
          <Stack gap="sm">
            <TextInput
              label="URL"
              placeholder="https://www.revisor.mn.gov/statutes/cite/144G.45"
              value={browser.fetchUrl}
              onChange={(e) => browser.setFetchUrl(e.currentTarget.value)}
              leftSection={<IconWorldWww size={14} />}
            />

            <Group grow>
              <TextInput
                label="CSS Selector (optional)"
                placeholder=".statute-text"
                value={browser.cssSelector}
                onChange={(e) => browser.setCssSelector(e.currentTarget.value)}
                description="Extract specific content from the page"
              />
              <Select
                label="Source Type"
                placeholder="Auto-detect"
                data={sourceTypeOptions}
                value={browser.sourceType || null}
                onChange={(val) => browser.setSourceType(val || '')}
                clearable
                description="Affects confidence scoring and review depth"
              />
            </Group>

            <Button
              onClick={() => browser.fetchFromUrl(
                browser.fetchUrl,
                browser.cssSelector || undefined,
                browser.sourceType || undefined,
              )}
              loading={browser.isFetching}
              disabled={!browser.fetchUrl}
              leftSection={<IconDownload size={14} />}
            >
              Fetch and Ingest
            </Button>

            {/* Quarantine scan results */}
            {browser.scanResult && (
              <QuarantineStatusCard
                stages={browser.scanResult.stages}
                overallStatus={browser.scanResult.overall_status}
              />
            )}

            {browser.scanStatus === 'failed' && (
              <Alert color="red" icon={<IconShieldOff size={16} />} title="Quarantine Blocked">
                Content was blocked by the security scanner. Review the scan results above.
              </Alert>
            )}
          </Stack>
        </Tabs.Panel>

        {/* ─── Adapter Sources Tab ────────────────────────── */}
        <Tabs.Panel value="adapter" pt="md">
          <Stack gap="sm">
            <Select
              label="Data Source Adapter"
              placeholder="Select adapter..."
              data={adapterOptions}
              value={browser.selectedAdapter}
              onChange={(val) => browser.setSelectedAdapter(val)}
              clearable
              searchable
              description="Loads web sources and enrichment context from adapter configuration"
            />

            {browser.adapterWebSources.length > 0 && (
              <Card withBorder padding="sm">
                <Text size="sm" fw={600} mb="xs">Web Sources</Text>
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Name</Table.Th>
                      <Table.Th>URL</Table.Th>
                      <Table.Th style={{ width: 80 }}>Action</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {browser.adapterWebSources.map((ws: WebSource) => (
                      <Table.Tr key={ws.name}>
                        <Table.Td>
                          <Text size="sm">{ws.name}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs" c="dimmed" truncate="end" style={{ maxWidth: 300 }}>
                            {ws.url}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Tooltip label={`Fetch from ${ws.url}`}>
                            <ActionIcon
                              variant="light"
                              color="blue"
                              size="sm"
                              onClick={() => browser.fetchWebSource(ws)}
                              loading={browser.isFetching}
                            >
                              <IconDownload size={14} />
                            </ActionIcon>
                          </Tooltip>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Card>
            )}

            {browser.selectedAdapter && browser.adapterWebSources.length === 0 && (
              <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
                This adapter has no web sources defined. Use the Upload or URL tab instead.
              </Alert>
            )}

            {!browser.selectedAdapter && (
              <Text size="sm" c="dimmed" ta="center" py="xl">
                Select an adapter to browse its configured web sources
              </Text>
            )}
          </Stack>
        </Tabs.Panel>
      </Tabs>

      {/* Adapter Context (shown below tabs for all modes) */}
      {browser.selectedAdapter && (
        <>
          <Divider />
          <Group gap="xs">
            <Text size="xs" c="dimmed">Adapter context:</Text>
            <Badge size="xs" variant="light">{browser.selectedAdapter}</Badge>
          </Group>
        </>
      )}
    </Stack>
  );
}

export default DataSourceBrowser;
